import {
  type GetUserQuery,
  getQueryClient,
  QUERY_KEYS,
  requiredFromListText,
} from "@frontend/roz-sdk-core";
import {
  UiButton,
  UiCheckbox,
  UiDateTimePicker,
  UiInput,
  UiText,
} from "@frontend/roz-uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useForm, useWatch } from "react-hook-form";

import { AddressSelect } from "@/lib/AddressSelect";
import { isAdult } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/primitives";
import { useSdk } from "@/provider";
import { useModalStore } from "@/ui-state";

import {
  type PersonalDataFormSchema,
  personalDataFormSchema,
} from "./personal-data-schema";

const MAX_BIRTHDATE_YEARS = 6;

export const PersonalDataForm = () => {
  const sdk = useSdk();
  const { close } = useModalStore();
  const client = getQueryClient();
  const user = client.getQueryData<GetUserQuery>(QUERY_KEYS.user)?.me;
  const queryClient = getQueryClient();

  const form = useForm<PersonalDataFormSchema>({
    resolver: zodResolver(personalDataFormSchema),
    defaultValues: {
      lastName: user?.lastName || "",
      firstName: user?.firstName || "",
      middleName: user?.middleName || "",
      birthDate: user?.birthDate ? new Date(user.birthDate) : undefined,
      noMiddleName: false,
      address: (user?.address as PersonalDataFormSchema["address"]) || {},
      agreement: false,
      agreementPolicy: false,
      promotionalMailings: false,
    },
  });

  const birthDateValue = useWatch({
    name: "birthDate",
    control: form.control,
  });

  const noMiddleNameValue = useWatch({
    name: "noMiddleName",
    control: form.control,
  });
  const addressValue = useWatch({
    name: "address",
    control: form.control,
  });

  const { mutateAsync: updateUser, isPending } = useMutation({
    mutationKey: QUERY_KEYS.updateUser,
    mutationFn: sdk.user.updateUser,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user }),
  });

  const isChild = !isAdult(birthDateValue) && !!birthDateValue;
  const hasAddressError = Boolean(form.formState.errors.address);
  const hasAddressValue = Boolean(addressValue?.value);
  const isAddressTouched = Boolean(form.formState.touchedFields.address);
  const showAddressHint = !(
    hasAddressError ||
    hasAddressValue ||
    isAddressTouched
  );

  const onSubmit = async (data: PersonalDataFormSchema) => {
    try {
      await updateUser({
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          middleName: data.middleName,
          noMiddleName: data.noMiddleName,
          birthDate: data.birthDate
            ? dayjs(data.birthDate).format("YYYY-MM-DD")
            : null,
          address: data.address,
          agreement: data.agreement,
          promotionalMailings: data.promotionalMailings,
        },
      });

      // старый код сразу вызывал refetch user
      // эмит выполняется, чтобы поддержать функционал в useUserFromSdk
      sdk.events.emit("updateUser");
      await sdk.user.refetchUser();
      close();

      //TODO: переписать этот костыль на utils.events и переделать на академии(ленде)
      if (globalThis.window.location.href.includes("akademiya")) {
        window.open("https://akademiyalms.znanierussia.ru/oauth/login/");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form className="sdkui:space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <UiInput data-testid="lastName" label="Фамилия" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <UiInput data-testid="firstName" label="Имя" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="middleName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <UiInput
                  data-testid="middleName"
                  label="Отчество"
                  {...field}
                  disabled={noMiddleNameValue}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="noMiddleName"
          render={({ field }) => (
            <FormItem>
              <div className="sdkui:flex sdkui:items-center sdkui:gap-2">
                <FormControl>
                  <UiCheckbox
                    checked={field.value}
                    data-testid="noMiddleName"
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;

                      form.setValue("noMiddleName", isChecked, {
                        shouldValidate: true,
                      });

                      if (isChecked) {
                        form.setValue("middleName", "", {
                          shouldValidate: true,
                        });
                        form.clearErrors("middleName");
                      }
                    }}
                  />
                </FormControl>
                <div>
                  <FormLabel>Нет отчества</FormLabel>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="birthDate"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <UiDateTimePicker
                  data-testid="birthDate"
                  maxDate={dayjs()
                    .subtract(MAX_BIRTHDATE_YEARS, "year")
                    .startOf("day")
                    .toDate()}
                  noPortal
                  onChange={field.onChange}
                  placeholder="Укажите дату рождения"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <AddressSelect
                  fieldName="address"
                  onChange={(region) => {
                    field.onChange(region);
                    form.clearErrors("address");
                  }}
                  onFocus={() => {
                    if (!field.value?.value) {
                      form.setValue("address", field.value || {}, {
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }
                  }}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
              {showAddressHint && (
                <UiText className="sdkui:text-dark-100 sdkui:text-sm">
                  {requiredFromListText}
                </UiText>
              )}
            </FormItem>
          )}
        />

        <div className="sdkui:flex sdkui:justify-center">
          <UiButton
            className="sdkui:w-full"
            data-testid="personal-data-submit-button"
            disabled={isPending}
            type="submit"
          >
            Продолжить
          </UiButton>
        </div>

        <div>
          {isChild && (
            <p className="sdkui:text-muted-foreground">
              Мне менее 18 лет, поэтому подтверждаю, что мой законный
              представитель (родитель/усыновитель/опекун):
            </p>
          )}
        </div>

        <FormField
          control={form.control}
          name="agreement"
          render={({ field }) => (
            <FormItem>
              <div className="sdkui:flex sdkui:items-start sdkui:gap-2">
                <FormControl>
                  <UiCheckbox
                    checked={field.value}
                    data-testid="personal-data-form-checkbox-agreement"
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div>
                  <FormLabel className="sdkui:font-light sdkui:text-muted-foreground sdkui:[&_a]:text-primary">
                    <div>
                      {isChild ? "Даёт " : "Я даю "}
                      <a
                        href="https://znan.ru/lektor-soglasie-na-obrabotku-personalnyh-dannyh"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Согласие на обработку моих персональных данных
                      </a>
                    </div>
                  </FormLabel>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="agreementPolicy"
          render={({ field }) => (
            <FormItem>
              <div className="sdkui:flex sdkui:items-start sdkui:gap-2">
                <FormControl>
                  <UiCheckbox
                    checked={field.value}
                    data-testid="personal-data-form-checkbox-agreement-policy"
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div>
                  <FormLabel className="sdkui:font-light sdkui:text-muted-foreground sdkui:[&_a]:text-primary">
                    <div>
                      {isChild
                        ? "Ознакомлен и принимает "
                        : "Ознакомлен и принимаю "}{" "}
                      условия{" "}
                      <a
                        href="https://roz-events.storage.yandexcloud.net/media/documents/15_03_2024_11_40_%D0%9F%D1%80_69_24_%D0%9F%D1%80%D0%B8%D0%BA%D0%B0%D0%B7_%D0%BE_%D0%B2%D0%B2%D0%B5%D0%B4%D0%B5%D0%BD%D0%B8%D0%B8_%D0%B2_%D0%B4%D0%B5%D0%B8%D1%81%D1%82%D0%B2%D0%B8%D0%B5_%D0%9F%D0%BE%D0%BB%D0%B8%D1%82%D0%B8%D0%BA%D0%B8.pdf"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Политики конфиденциальности
                      </a>{" "}
                      и{" "}
                      <a
                        href="https://znan.ru/polzovatelskoe-soglashenie-ob-ispolzovanii-sajta"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Пользовательского соглашения
                      </a>
                    </div>
                  </FormLabel>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="promotionalMailings"
          render={({ field }) => (
            <FormItem>
              <div className="sdkui:flex sdkui:items-start sdkui:gap-2">
                <FormControl>
                  <UiCheckbox
                    checked={field.value}
                    data-testid="personal-data-form-checkbox-promotional-mailings"
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div>
                  <FormLabel className="sdkui:font-light sdkui:text-muted-foreground">
                    {isChild ? "Дает " : "Я даю "} согласие на{"\u00A0"}
                    получение рассылки рекламного, информационного характера от
                    {"\u00A0"}Общероссийской общественно-государственной
                    просветительской организации „Российское общество «Знание»
                    (ИНН{"\u00A0"}7706439561) и{"\u00A0"}уполномоченных ею
                    {"\u00A0"}на
                    {"\u00A0"}то{"\u00A0"}лиц на{"\u00A0"}указанный мною
                    электронный адрес и{"\u00A0"}номер телефона, с{"\u00A0"}
                    целью информирования об{"\u00A0"}иных мероприятиях
                    Организации.
                  </FormLabel>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
